
import type { QuizSchema } from "@/types/quiz";

/**
 * Scoring model notes (altsiq_score_v1.3):
 * - Tolerance domain (emotional/behavioural): q4, q5, q6, q8, q9  -> tolerance_score (0–100)
 * - Capacity domain (portfolio ability based on financial profile): q10–q14 -> capacity_score (0–100)
 * - Each domain is normalized to 0–100 by (sum of chosen values) / (sum of max values) * 100.
 * - For multi-select (e.g., q7), the domain contribution = MAX(option values selected).
 *   (Prevents double-count bias; unchanged from your current behaviour.)
 * - You can still compute a composite if needed, but primary UX should use the
 *   Tolerance (Y) vs Capacity (X) quadrant and route CTAs accordingly.
 */

const quiz: QuizSchema = {
  version: "v2.1",
  updated_at: "2025-11-07",
  locale: "en-CA",
  scoring_formula_ref: "altsiq_score_v1.3",
  questions: [
    // ---------------- Existing questions (v1.9) ----------------
    {
      id: "q1_duration",
      connector:
        "Let's start with how long you usually think about keeping your money invested before touching it.",
      question:
        "When making a long-term investment, I plan to keep the money invested for...",
      options: [
        { label: "2 years or less", value: 0 },
        { label: "3-5 years", value: 1 },
        { label: "6-10 years", value: 2 },
        { label: "11-15 years", value: 3 },
        { label: "More than 15 years", value: 4 }
      ],
      why: "The length of time you plan to stay invested plays a key role in determining how much short-term volatility you can comfortably manage. Time horizon affects how much risk is appropriate and how long your investments have to recover from market swings.",
      meaning:
        "If you expect to use your money within a few years, maintaining a more stable, income-oriented mix generally helps protect against market fluctuations.\n\nIf you're investing for a decade or longer, you have more flexibility to hold assets with higher long-term return potential, even if their value moves up and down in the short term.",
      dimension: "capacity",
    },
    {
      id: "q2_access",
      connector: "Everyone sometimes needs cash for opportunities or surprises.",
      question:
        "How likely are you to need access to this money within the next year?",
      options: [
        { label: "Likely - I may need a portion", value: 0 },
        { label: "Unlikely - I don't expect to touch it", value: 2 },
        { label: "Very unlikely - I'm investing for the long term", value: 4 }
      ],
      why: "Your need for ready access to money helps identify how much of your portfolio should emphasize stability versus growth. Liquidity is one of the most practical influences on risk tolerance.",
      meaning:
        "Investors who expect to use their funds sooner often prefer portfolios that focus on preserving value and providing easier access.\n\nInvestors who won't need their money for many years can generally tolerate more fluctuation in pursuit of longer-term growth potential.\n\nUnderstanding this balance helps clarify how liquidity needs connect to investment time frames.",
      dimension: "capacity",
    },
    {
      id: "q3_income",
      connector: "Income stability often shapes how much risk feels comfortable.",
      question:
        "How confident do you feel about your current and future income sources? (Examples: salary, business, or investment income.)",
      options: [
        { label: "Not confident", value: 0 },
        { label: "Somewhat uncertain", value: 1 },
        { label: "Confident", value: 3 },
        { label: "Very confident", value: 4 }
      ],
      why: "Your current and future income stability forms the foundation for your investing comfort. Reliable earnings can make it easier to stay invested through normal market ups and downs, while variable income can make steadier returns feel more appropriate.",
      meaning:
        "Your asset mix could lean more conservative or more growth-oriented depending on your financial outlook. A stable income base often increases an investor's ability to take risk; less predictability tends to make a steadier approach feel more sustainable over time.\n\nThis question helps connect your cash-flow reality to your broader investing temperament.",
      dimension: "capacity",
    },
    {
      id: "q4_loss_reaction",
      connector:
        "Imagine the market drops right after you invest - what feels true to you?",
      question:
        "If your $10,000 investment dropped by 20% within a few months, what would you most likely do?",
      options: [
        { label: "Sell some or all to reduce loss", value: 0 },
        { label: "Stay invested and wait it out", value: 2 },
        { label: "Buy more while prices are lower", value: 4 }
      ],
      why: "How you respond to a market decline reveals your practical comfort with volatility. Emotional reactions often provide a clearer picture of true risk tolerance than theoretical preferences do.",
      meaning:
        "If a market drop would prompt you to sell, it suggests a preference for steadier investments that protect value in exchange for lower potential returns.\n\nIf you'd hold or even invest more during a downturn, it indicates greater willingness to accept short-term fluctuations for long-term growth potential.",
      dimension: "tolerance",
    },
    {
      id: "q5_growth_vs_stability",
      connector:
        "Everyone balances peace of mind with potential returns a little differently.",
      question: "Which statement feels most like you when investing?",
      options: [
        {
          label: "I'd rather protect my money than chase higher returns",
          value: 0
        },
        { label: "I'm comfortable with a balanced approach", value: 2 },
        {
          label: "I prefer higher growth even with more volatility swings",
          value: 4
        }
      ],
      why: "Every investor balances the desire for growth with the need for stability. Understanding where you fall on that spectrum helps frame your comfort with risk and return trade-offs.",
      meaning:
        "Choosing stability suggests a comfort zone focused on capital preservation and consistent returns.\n\nPreferring growth indicates greater tolerance for market ups and downs in exchange for higher long-term return potential.\n\nThis helps shape an asset mix that aligns with your comfort level rather than a one-size-fits-all model.",
      dimension: "preference",
    },
    {
      id: "q6_understanding",
      connector:
        "No wrong answers here - we just want to tailor things to your level of experience.",
      question:
        "On a scale of 1-5, how well do you think you understand investing in financial markets? (Think mutual funds, ETFs, equities, bonds, etc.)",
      options: [
        { label: "1 - Novice", value: 0 },
        { label: "2 - Advanced beginner", value: 1 },
        { label: "3 - Competent", value: 2 },
        { label: "4 - Proficient", value: 3 },
        { label: "5 - Expert", value: 4 }
      ],
      why: "Your familiarity with investing concepts influences how confident you feel when making financial decisions. Experience affects how you interpret risk, diversification, and performance over time.",
      meaning:
        "If you're newer to investing, you may prefer portfolios that are simpler, broadly diversified, and easy to monitor.\n\nIf you have more experience, you may be comfortable evaluating a wider range of investment types, time horizons, and performance dynamics.\n\nRecognizing your comfort level helps ensure insights are framed at the right level of detail.",
      dimension: "tolerance",
    },
    {
      id: "q7_investing_style",
      connector: "Different investors like different levels of involvement.",
      question: "How do you typically invest? (Select all that apply.)",
      options: [
        {
          label:
            "Actively manage my portfolio (e.g., buy individual stocks or bonds)",
          value: 4
        },
        { label: "Passively invest (e.g., ETFs or mutual funds)", value: 2 },
        { label: "Partner with a financial advisor", value: 3 }
      ],
      multi_select: true,
      why: "How you approach investing reflects how you prefer to make decisions - independently, collaboratively, or through professional guidance. Each approach involves different levels of involvement, confidence, and reliance on outside expertise.",
      meaning:
        "If you prefer a hands-on role, your results can highlight tools and insights designed for self-directed investors.\n\nIf you invest through advisors or managed solutions, your profile can help clarify how risk tolerance and portfolio structure align - even when decisions are made on your behalf.\n\nThis question simply helps contextualize how you interact with your investments.",
      dimension: "preference",
    },
    {
      id: "q8_lockup_comfort",
      connector:
        "Some investments take several years before you can access your money.",
      question:
        "How comfortable are you committing a portion of your portfolio for several years to pursue higher potential returns?",
      options: [
        { label: "Not comfortable", value: 0 },
        { label: "Somewhat comfortable", value: 2 },
        { label: "Comfortable with longer lockups", value: 4 }
      ],
      conditional_display: "q1_duration >= 2",
      why: "Some investment opportunities - particularly in private markets - require longer holding periods before you can access your money. Understanding your comfort with that trade-off helps gauge asset-allocation suitability and patience for illiquidity.",
      meaning:
        "If you prefer ready access to your money, your profile may emphasize liquid investments that can be adjusted as needed.\n\nIf you're comfortable with longer commitments, it indicates tolerance for strategies that reward patience with potentially higher or more stable long-term returns.",
      dimension: "tolerance",
    },
    {
      id: "q9_private_markets",
      connector:
        "Private markets can include opportunities that most investors can't easily access.",
      question:
        "Are you interested in exploring private-market opportunities - like infrastructure, private credit, or hedge funds that aren't traded daily?",
      options: [
        { label: "Not interested right now", value: 0 },
        { label: "Curious to learn more", value: 2 },
        { label: "Interested in including them", value: 4 }
      ],
      conditional_display: "q1_duration >= 2",
      why: "Private-market investments - such as infrastructure, private credit, or hedge funds - can behave differently from traditional public markets. They can add diversification but also introduce unique risks and longer lock-ups.\n\nThis question helps identify your familiarity and comfort with investments that are less liquid and more specialized than traditional stocks or bonds.\n\nMany investors also hold private assets through their own ventures, real estate, or other long-term holdings - which can create unrecognized concentration risk.",
      meaning:
        "Interest in private markets may indicate openness to diversification beyond public equities and bonds. If your interest is limited for now, your results will focus on public equities, fixed income (bonds), and liquid strategies - the foundation of most diversified portfolios. Understanding your perspective helps clarify whether and when private-market exposure might complement your broader investment approach.",
      dimension: "tolerance",
    },

    // ---------------- New Risk Capacity block (v2.1) ----------------
    // Tone focuses on the portfolio’s ability to sustain variability based on observable data,
    // inviting the account/holding connection as the logical CTA to compute capacity precisely.

    {
      id: "q10_rc_income_stability",
      connector:
        "Next, let’s estimate your portfolio’s ability to sustain variability based on your financial profile.",
      question:
        "Over the next 3 years, how consistent do you expect your household income to be?",
      options: [
        { label: "Highly consistent and predictable", value: 4 },
        { label: "Mostly consistent with minor fluctuations", value: 3 },
        { label: "Moderately variable with business/market cycles", value: 2 },
        { label: "Uncertain or frequently variable", value: 0 }
      ],
      why:
        "Reliable income can help a portfolio maintain course through normal volatility, while variable income often calls for a steadier allocation.",
      meaning:
        "Income consistency contributes to capacity because it reduces the likelihood that market fluctuations force portfolio changes at inconvenient times.",
      dimension: "capacity",
    },
    {
      id: "q11_rc_liquidity_buffer",
      connector:
        "Liquidity cushions help portfolios tolerate normal ups and downs without forced selling.",
      question:
        "If your primary income stopped, how long could you maintain your lifestyle without selling investments?",
      options: [
        { label: "More than 12 months", value: 4 },
        { label: "6–12 months", value: 3 },
        { label: "3–6 months", value: 2 },
        { label: "Less than 3 months", value: 0 }
      ],
      why:
        "A larger cash buffer increases portfolio flexibility and supports longer holding periods.",
      meaning:
        "Liquidity reduces the chance that short-term volatility translates into permanent loss via forced liquidation.",
      dimension: "capacity",
    },
    {
      id: "q12_rc_time_to_use",
      connector:
        "Time horizon determines how much short-term variability a portfolio can absorb.",
      question:
        "When do you expect to use a significant portion of these invested funds?",
      options: [
        { label: "10+ years", value: 4 },
        { label: "6–10 years", value: 3 },
        { label: "3–5 years", value: 2 },
        { label: "Within 1–2 years", value: 0 }
      ],
      why:
        "Longer horizons generally expand the set of viable strategies, including those with limited liquidity.",
      meaning:
        "More time allows compounding and recovery; less time often calls for lower-volatility, higher-liquidity allocations.",
      dimension: "capacity",
    },
    {
      id: "q13_rc_investable_share",
      connector:
        "Capacity also depends on how much of your net worth is truly investable.",
      question:
        "What share of your total net worth (excluding your primary residence) is available for investment?",
      options: [
        { label: "Over 75%", value: 4 },
        { label: "50–75%", value: 3 },
        { label: "25–50%", value: 2 },
        { label: "Less than 25%", value: 0 }
      ],
      why:
        "A larger investable base typically allows more room to diversify across liquidity profiles.",
      meaning:
        "When investable assets are a small share of net worth, allocations often prioritize stability and access.",
      dimension: "capacity",
    },
    {
      id: "q14_rc_withdrawals",
      connector:
        "Finally, expected withdrawals shape how much variability is sustainable.",
      question:
        "Over the next few years, how much will you rely on portfolio withdrawals to cover living expenses?",
      options: [
        { label: "Not at all", value: 4 },
        { label: "Occasionally", value: 3 },
        { label: "Regularly", value: 1 },
        { label: "Depend heavily on withdrawals", value: 0 }
      ],
      why:
        "The more a portfolio must fund near-term expenses, the more limited its tolerance for drawdowns and lockups.",
      meaning:
        "Lower withdrawal needs usually increase capacity for longer-term, less-liquid strategies.",
      dimension: "capacity",
    }
  ]
};

export default quiz;
